// Badge definitions for the reseller achievement system
// Each badge has requirements that are checked against reseller stats

export const BADGE_CATEGORIES = {
  USERS: "users",
  DOMAINS: "domains",
  LOYALTY: "loyalty",
  PERFORMANCE: "performance",
  SPECIAL: "special",
};

// Badge progression groups - badges in the same group are part of a progression
// Only the highest earned badge in each progression is shown in the main view
export const BADGE_PROGRESSIONS = {
  USERS: "users_progression",
  DOMAINS: "domains_progression",
  LOYALTY: "loyalty_progression",
  TIERS: "tiers_progression",
  WEEKLY_GROWTH: "weekly_growth_progression",
  CHAMPION: "champion_progression", // Sunburst elite badges
  RETENTION: "retention_progression", // No churn streaks
  GROWTH_EXPLOSION: "growth_explosion_progression", // Massive weekly growth
  DOMAIN_MASTERY: "domain_mastery_progression", // Domain overlords
  TITAN: "titan_progression", // 2000+ users elite tier
  // Other special badges don't have progressions - they're standalone
};

export const BADGE_RARITY = {
  COMMON: { name: "Common", color: "gray", glow: "rgba(156, 163, 175, 0.5)" },
  UNCOMMON: {
    name: "Uncommon",
    color: "green",
    glow: "rgba(34, 197, 94, 0.5)",
  },
  RARE: { name: "Rare", color: "blue", glow: "rgba(59, 130, 246, 0.5)" },
  EPIC: { name: "Epic", color: "purple", glow: "rgba(168, 85, 247, 0.5)" },
  LEGENDARY: {
    name: "Legendary",
    color: "orange",
    glow: "rgba(249, 115, 22, 0.5)",
  },
  MYTHIC: { name: "Mythic", color: "pink", glow: "rgba(236, 72, 153, 0.5)" },
};

// Badge gradient colors for the octagon fill
const BADGE_GRADIENTS = {
  // User milestone badges
  bronze: ["#CD7F32", "#8B4513", "#D4A574"],
  silver: ["#C0C0C0", "#A8A8A8", "#E8E8E8"],
  gold: ["#FFD700", "#FFA500", "#FFEC8B"],
  diamond: ["#B9F2FF", "#87CEEB", "#E0FFFF"],
  ruby: ["#E0115F", "#9B111E", "#FF6B6B"],
  cosmic: ["#2E1A47", "#7B2D8E", "#FF6EC7"],
  // Domain badges
  teal: ["#008080", "#006666", "#20B2AA"],
  sapphire: ["#0F52BA", "#082567", "#6495ED"],
  violet: ["#8B00FF", "#6A0DAD", "#BF40BF"],
  // Loyalty badges
  copper: ["#B87333", "#8B5A2B", "#DAA06D"],
  steel: ["#71797E", "#5A6268", "#A7B0B7"],
  amber: ["#FFBF00", "#CC9900", "#FFD54F"],
  platinum: ["#E5E4E2", "#A0B2C6", "#D4D4D4"],
  // Performance badges
  emerald: ["#50C878", "#2E8B57", "#98FB98"],
  electric: ["#00BFFF", "#0080FF", "#87CEFA"],
  crimson: ["#DC143C", "#B22222", "#FF6B6B"],
  aquamarine: ["#7FFFD4", "#40E0D0", "#AFEEEE"],
  // Special badges
  obsidian: ["#0B1215", "#1C1C1C", "#3D3D3D"],
  citrine: ["#E4D00A", "#B8A700", "#F5E050"],
  amethyst: ["#9966CC", "#6B3FA0", "#DDA0DD"],
  coral: ["#FF7F50", "#FF6347", "#FFA07A"],
  turbo: ["#00FF87", "#00D4AA", "#7FFFD4"],
  // Sunburst badge colors
  sunburstBronze: ["#CD7F32", "#8B4513", "#E8A857"],
  sunburstSilver: ["#C0C0C0", "#8A8A8A", "#E8E8E8"],
  sunburstGold: ["#FFD700", "#B8860B", "#FFEC8B"],
  sunburstPlatinum: ["#E5E4E2", "#A0B2C6", "#F5F5F5"],
  // Retention badges - icy/diamond theme
  ironGrip: ["#5C6D7E", "#3D4F5F", "#8899AA"],
  diamondHands: ["#B9F2FF", "#87CEEB", "#E0FFFF"],
  unbreakable: ["#2E1A47", "#7B2D8E", "#FF6EC7"],
  // Growth explosion badges - fire theme
  rocketLaunch: ["#FF4500", "#FF6347", "#FFA500"],
  hypergrowth: ["#FF0000", "#8B0000", "#FF6B6B"],
  // OG Status - ancient/legendary
  ogStatus: ["#1C1C1C", "#4A0E0E", "#8B4513"],
  // Domain mastery - cosmic theme
  domainOverlord: ["#4B0082", "#8A2BE2", "#9370DB"],
  domainGod: ["#0D0D0D", "#1A1A2E", "#7B2D8E"],
  // Combo badges - rainbow/multi-color
  perfectStorm: ["#FF6B6B", "#4ECDC4", "#45B7D1"],
  unicorn: ["#FF69B4", "#FF1493", "#FFD700"],
  // Titan badges - massive scale
  titan: ["#2F4F4F", "#556B2F", "#8B4513"],
  godlike: ["#0D0D0D", "#FFD700", "#8B0000"],
};

// Center orb colors for sunburst badges
export const SUNBURST_CENTER_COLORS = {
  green: ["#4CAF50", "#2E7D32", "#81C784"],
  blue: ["#2196F3", "#1565C0", "#64B5F6"],
  purple: ["#9C27B0", "#6A1B9A", "#CE93D8"],
  red: ["#F44336", "#C62828", "#EF9A9A"],
  orange: ["#FF9800", "#E65100", "#FFB74D"],
  teal: ["#009688", "#00695C", "#4DB6AC"],
};

export const BADGES = [
  // ===== USER MILESTONE BADGES =====
  {
    id: "first_ten",
    name: "First Ten",
    description: "Reached 10 active users",
    longDescription:
      "You've successfully onboarded your first 10 users. This is just the beginning of your reseller journey!",
    category: BADGE_CATEGORIES.USERS,
    rarity: BADGE_RARITY.COMMON,
    icon: "fa-alien-8bit",
    gradient: BADGE_GRADIENTS.bronze,
    requirement: { type: "min_users", value: 10 },
    progression: BADGE_PROGRESSIONS.USERS,
    progressionOrder: 1,
    order: 1,
  },
  {
    id: "quarter_century",
    name: "Quarter Century",
    description: "Reached 25 active users",
    longDescription:
      "25 users and counting! You've officially entered reseller territory.",
    category: BADGE_CATEGORIES.USERS,
    rarity: BADGE_RARITY.UNCOMMON,
    icon: "fa-alien-8bit",
    gradient: BADGE_GRADIENTS.silver,
    requirement: { type: "min_users", value: 25 },
    progression: BADGE_PROGRESSIONS.USERS,
    progressionOrder: 2,
    order: 2,
  },
  {
    id: "half_century",
    name: "Half Century",
    description: "Reached 50 active users",
    longDescription:
      "50 users! You're building a real community around Lunova Player.",
    category: BADGE_CATEGORIES.USERS,
    rarity: BADGE_RARITY.RARE,
    icon: "fa-alien-8bit",
    gradient: BADGE_GRADIENTS.gold,
    requirement: { type: "min_users", value: 50 },
    progression: BADGE_PROGRESSIONS.USERS,
    progressionOrder: 3,
    order: 3,
  },
  {
    id: "centurion",
    name: "Centurion",
    description: "Reached 100 active users",
    longDescription:
      "Triple digits! 100 users trust you as their Lunova provider.",
    category: BADGE_CATEGORIES.USERS,
    rarity: BADGE_RARITY.EPIC,
    icon: "fa-crown",
    gradient: BADGE_GRADIENTS.diamond,
    requirement: { type: "min_users", value: 100 },
    progression: BADGE_PROGRESSIONS.USERS,
    progressionOrder: 4,
    order: 4,
  },
  {
    id: "empire_builder",
    name: "Empire Builder",
    description: "Reached 500 active users",
    longDescription:
      "500 users! You've built a true streaming empire. Legendary status achieved.",
    category: BADGE_CATEGORIES.USERS,
    rarity: BADGE_RARITY.LEGENDARY,
    icon: "fa-chess-king",
    gradient: BADGE_GRADIENTS.ruby,
    requirement: { type: "min_users", value: 500 },
    progression: BADGE_PROGRESSIONS.USERS,
    progressionOrder: 5,
    order: 5,
  },
  {
    id: "legendary",
    name: "Legendary",
    description: "Reached 1000 active users",
    longDescription:
      "1000 users! You are now a legend in the Lunova ecosystem. The mythic badge is yours.",
    category: BADGE_CATEGORIES.USERS,
    rarity: BADGE_RARITY.MYTHIC,
    icon: "fa-star",
    gradient: BADGE_GRADIENTS.cosmic,
    requirement: { type: "min_users", value: 1000 },
    progression: BADGE_PROGRESSIONS.USERS,
    progressionOrder: 6,
    order: 6,
  },

  // ===== DOMAIN EXPANSION BADGES =====
  {
    id: "multi_domain",
    name: "Multi-Domain",
    description: "Added 2+ service domains",
    longDescription:
      "You've expanded to multiple domains, offering variety to your users.",
    category: BADGE_CATEGORIES.DOMAINS,
    rarity: BADGE_RARITY.UNCOMMON,
    icon: "fa-globe",
    gradient: BADGE_GRADIENTS.teal,
    requirement: { type: "min_domains", value: 2 },
    progression: BADGE_PROGRESSIONS.DOMAINS,
    progressionOrder: 1,
    order: 7,
  },
  {
    id: "domain_collector",
    name: "Domain Collector",
    description: "Added 5+ service domains",
    longDescription:
      "5 domains! You're a true collector, providing maximum options for your users.",
    category: BADGE_CATEGORIES.DOMAINS,
    rarity: BADGE_RARITY.RARE,
    icon: "fa-layer-group",
    gradient: BADGE_GRADIENTS.sapphire,
    requirement: { type: "min_domains", value: 5 },
    progression: BADGE_PROGRESSIONS.DOMAINS,
    progressionOrder: 2,
    order: 8,
  },
  {
    id: "domain_empire",
    name: "Domain Empire",
    description: "Added 10+ service domains",
    longDescription:
      "10 domains under your belt! You've built a domain empire that rivals the best.",
    category: BADGE_CATEGORIES.DOMAINS,
    rarity: BADGE_RARITY.EPIC,
    icon: "fa-sitemap",
    gradient: BADGE_GRADIENTS.violet,
    requirement: { type: "min_domains", value: 10 },
    progression: BADGE_PROGRESSIONS.DOMAINS,
    progressionOrder: 3,
    order: 9,
  },

  // ===== LOYALTY BADGES =====
  {
    id: "first_month",
    name: "First Month",
    description: "30 days as a reseller",
    longDescription:
      "You've been a reseller for a full month. Thanks for sticking with us!",
    category: BADGE_CATEGORIES.LOYALTY,
    rarity: BADGE_RARITY.COMMON,
    icon: "fa-calendars",
    gradient: BADGE_GRADIENTS.copper,
    requirement: { type: "min_days", value: 30 },
    progression: BADGE_PROGRESSIONS.LOYALTY,
    progressionOrder: 1,
    order: 10,
  },
  {
    id: "quarterly_veteran",
    name: "Quarterly Veteran",
    description: "90 days as a reseller",
    longDescription:
      "3 months of dedication! You're now a quarterly veteran in the Lunova family.",
    category: BADGE_CATEGORIES.LOYALTY,
    rarity: BADGE_RARITY.UNCOMMON,
    icon: "fa-calendars",
    gradient: BADGE_GRADIENTS.steel,
    requirement: { type: "min_days", value: 90 },
    progression: BADGE_PROGRESSIONS.LOYALTY,
    progressionOrder: 2,
    order: 11,
  },
  {
    id: "annual_partner",
    name: "Annual Partner",
    description: "365 days as a reseller",
    longDescription:
      "A full year! You're an annual partner and a cornerstone of our community.",
    category: BADGE_CATEGORIES.LOYALTY,
    rarity: BADGE_RARITY.EPIC,
    icon: "fa-calendars",
    gradient: BADGE_GRADIENTS.amber,
    requirement: { type: "min_days", value: 365 },
    progression: BADGE_PROGRESSIONS.LOYALTY,
    progressionOrder: 3,
    order: 12,
  },
  {
    id: "founding_member",
    name: "Founding Member",
    description: "2+ years as a reseller",
    longDescription:
      "Two years of partnership! You're a founding member and a true legend.",
    category: BADGE_CATEGORIES.LOYALTY,
    rarity: BADGE_RARITY.LEGENDARY,
    icon: "fa-medal",
    gradient: BADGE_GRADIENTS.platinum,
    requirement: { type: "min_days", value: 730 },
    progression: BADGE_PROGRESSIONS.LOYALTY,
    progressionOrder: 4,
    order: 13,
  },

  // ===== PERFORMANCE BADGES =====
  {
    id: "first_tier",
    name: "First Tier",
    description: "Reached Nano tier",
    longDescription:
      "You've reached the Nano tier! This is your first step into tiered benefits.",
    category: BADGE_CATEGORIES.PERFORMANCE,
    rarity: BADGE_RARITY.UNCOMMON,
    icon: "fa-rocket",
    gradient: BADGE_GRADIENTS.emerald,
    requirement: { type: "min_users", value: 50 },
    progression: BADGE_PROGRESSIONS.TIERS,
    progressionOrder: 1,
    order: 14,
  },
  {
    id: "rising_star",
    name: "Rising Star",
    description: "Reached Starter tier or higher",
    longDescription:
      "You're a rising star in the reseller community. The Starter tier unlocks new opportunities.",
    category: BADGE_CATEGORIES.PERFORMANCE,
    rarity: BADGE_RARITY.RARE,
    icon: "fa-bolt",
    gradient: BADGE_GRADIENTS.electric,
    requirement: { type: "min_tier_order", value: 2 },
    progression: BADGE_PROGRESSIONS.TIERS,
    progressionOrder: 2,
    order: 15,
  },
  {
    id: "power_seller",
    name: "Power Seller",
    description: "Reached Basic tier or higher",
    longDescription:
      "Power Seller status achieved! You're in the top echelon of resellers.",
    category: BADGE_CATEGORIES.PERFORMANCE,
    rarity: BADGE_RARITY.EPIC,
    icon: "fa-swords",
    gradient: BADGE_GRADIENTS.crimson,
    requirement: { type: "min_tier_order", value: 3 },
    progression: BADGE_PROGRESSIONS.TIERS,
    progressionOrder: 3,
    order: 16,
  },
  {
    id: "elite_reseller",
    name: "Elite Reseller",
    description: "Reached Pro tier or higher",
    longDescription:
      "Elite status! You've proven yourself as one of the best resellers.",
    category: BADGE_CATEGORIES.PERFORMANCE,
    rarity: BADGE_RARITY.LEGENDARY,
    icon: "fa-beer-mug",
    gradient: BADGE_GRADIENTS.aquamarine,
    requirement: { type: "min_tier_order", value: 6 },
    progression: BADGE_PROGRESSIONS.TIERS,
    progressionOrder: 4,
    order: 17,
  },

  // ===== SPECIAL BADGES =====
  {
    id: "early_adopter",
    name: "Early Adopter",
    description: "Joined before 2025",
    longDescription:
      "You were one of the early adopters! Thanks for believing in us from the start.",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.LEGENDARY,
    icon: "fa-clock-rotate-left",
    gradient: BADGE_GRADIENTS.obsidian,
    requirement: { type: "joined_before", value: "2025-01-01" },
    order: 18,
  },
  {
    id: "growth_spurt",
    name: "Growth Spurt",
    description: "Added 10+ users in one week",
    longDescription:
      "You added 10+ users in a single week! That's some serious growth momentum.",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.RARE,
    icon: "fa-chart-line",
    gradient: BADGE_GRADIENTS.citrine,
    requirement: { type: "weekly_growth", value: 10 },
    progression: BADGE_PROGRESSIONS.WEEKLY_GROWTH,
    progressionOrder: 1,
    order: 19,
  },
  {
    id: "perfect_retention",
    name: "Perfect Retention",
    description: "No user churn for 30+ days",
    longDescription:
      "30 days without losing a single user! Your retention game is strong.",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.EPIC,
    icon: "fa-shield-check",
    gradient: BADGE_GRADIENTS.amethyst,
    requirement: { type: "no_churn_days", value: 30 },
    order: 20,
  },
  {
    id: "steady_grower",
    name: "Steady Grower",
    description: "Maintained slow but consistent growth",
    longDescription:
      "Slow and steady wins the race! You've been consistently adding users over time without rushing.",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.UNCOMMON,
    icon: "fa-seedling",
    gradient: BADGE_GRADIENTS.coral,
    requirement: { type: "slow_growth", value: 30 },
    order: 21,
  },
  {
    id: "turbo_reseller",
    name: "Turbo Reseller",
    description: "Added 25+ users in one week",
    longDescription:
      "You're on fire! Adding 25+ users in a single week is an incredible achievement. Keep up the momentum!",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.EPIC,
    icon: "fa-jet-fighter",
    gradient: BADGE_GRADIENTS.turbo,
    requirement: { type: "weekly_growth", value: 25 },
    progression: BADGE_PROGRESSIONS.WEEKLY_GROWTH,
    progressionOrder: 2,
    order: 22,
  },

  // ===== SUNBURST STYLE BADGES (Champion Progression) =====
  {
    id: "champion_reseller",
    name: "Champion Reseller",
    description: "Achieved top 10% reseller status",
    longDescription:
      "You've risen to the top 10% of all resellers! Your dedication and growth have made you a champion in the Lunova community.",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.LEGENDARY,
    icon: "fa-trophy",
    gradient: BADGE_GRADIENTS.sunburstBronze,
    centerGradient: ["#4CAF50", "#2E7D32", "#81C784"],
    badgeType: "sunburst",
    requirement: { type: "min_users", value: 250 },
    progression: BADGE_PROGRESSIONS.CHAMPION,
    progressionOrder: 1,
    order: 23,
  },
  {
    id: "master_reseller",
    name: "Master Reseller",
    description: "Achieved top 5% reseller status",
    longDescription:
      "You're in the elite top 5% of resellers! Your mastery of the platform and exceptional growth set you apart from the rest.",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.MYTHIC,
    icon: "fa-gem",
    gradient: BADGE_GRADIENTS.sunburstGold,
    centerGradient: ["#9C27B0", "#6A1B9A", "#CE93D8"],
    badgeType: "sunburst",
    requirement: { type: "min_users", value: 750 },
    progression: BADGE_PROGRESSIONS.CHAMPION,
    progressionOrder: 2,
    order: 24,
  },

  // ===== RETENTION MASTERY PROGRESSION =====
  {
    id: "iron_grip",
    name: "Iron Grip",
    description: "No user churn for 60+ days",
    longDescription:
      "60 days of perfect retention! Your users are locked in and loving it. Your service quality speaks for itself.",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.EPIC,
    icon: "fa-hand-fist",
    gradient: BADGE_GRADIENTS.ironGrip,
    requirement: { type: "no_churn_days", value: 60 },
    progression: BADGE_PROGRESSIONS.RETENTION,
    progressionOrder: 1,
    order: 25,
  },
  {
    id: "diamond_hands",
    name: "Diamond Hands",
    description: "No user churn for 90+ days",
    longDescription:
      "90 days without losing a single user! Your diamond hands have kept every customer. Legendary retention achieved.",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.LEGENDARY,
    icon: "fa-gem",
    gradient: BADGE_GRADIENTS.diamondHands,
    requirement: { type: "no_churn_days", value: 90 },
    progression: BADGE_PROGRESSIONS.RETENTION,
    progressionOrder: 2,
    order: 26,
  },
  {
    id: "unbreakable",
    name: "Unbreakable",
    description: "No user churn for 180+ days",
    longDescription:
      "Half a year of zero churn! You've achieved the impossible. Your retention is truly unbreakable.",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.MYTHIC,
    icon: "fa-shield-halved",
    gradient: BADGE_GRADIENTS.unbreakable,
    badgeType: "sunburst",
    centerGradient: ["#B9F2FF", "#87CEEB", "#E0FFFF"],
    requirement: { type: "no_churn_days", value: 180 },
    progression: BADGE_PROGRESSIONS.RETENTION,
    progressionOrder: 3,
    order: 27,
  },

  // ===== GROWTH EXPLOSION PROGRESSION =====
  {
    id: "rocket_launch",
    name: "Rocket Launch",
    description: "Added 50+ users in one week",
    longDescription:
      "50 users in a single week! Your growth has gone parabolic. You've launched like a rocket!",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.LEGENDARY,
    icon: "fa-rocket",
    gradient: BADGE_GRADIENTS.rocketLaunch,
    requirement: { type: "weekly_growth", value: 50 },
    progression: BADGE_PROGRESSIONS.GROWTH_EXPLOSION,
    progressionOrder: 1,
    order: 28,
  },
  {
    id: "hypergrowth",
    name: "Hypergrowth",
    description: "Added 100+ users in one week",
    longDescription:
      "100 users in ONE WEEK! This is hypergrowth at its finest. You're operating at a level few will ever reach.",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.MYTHIC,
    icon: "fa-fire-flame-curved",
    gradient: BADGE_GRADIENTS.hypergrowth,
    badgeType: "sunburst",
    centerGradient: ["#FF4500", "#FF6347", "#FFA500"],
    requirement: { type: "weekly_growth", value: 100 },
    progression: BADGE_PROGRESSIONS.GROWTH_EXPLOSION,
    progressionOrder: 2,
    order: 29,
  },

  // ===== OG STATUS (Extends Loyalty Progression) =====
  {
    id: "og_status",
    name: "OG Status",
    description: "3+ years as a reseller",
    longDescription:
      "Three years of loyalty! You're an Original Gangster in the Lunova ecosystem. Your dedication is unmatched.",
    category: BADGE_CATEGORIES.LOYALTY,
    rarity: BADGE_RARITY.MYTHIC,
    icon: "fa-skull",
    gradient: BADGE_GRADIENTS.ogStatus,
    badgeType: "sunburst",
    centerGradient: ["#FFD700", "#B8860B", "#FFEC8B"],
    requirement: { type: "min_days", value: 1095 },
    progression: BADGE_PROGRESSIONS.LOYALTY,
    progressionOrder: 5,
    order: 30,
  },

  // ===== DOMAIN MASTERY PROGRESSION =====
  {
    id: "domain_overlord",
    name: "Domain Overlord",
    description: "Added 20+ service domains",
    longDescription:
      "20 domains under your control! You're not just collecting domains - you're ruling them. Domain Overlord status achieved.",
    category: BADGE_CATEGORIES.DOMAINS,
    rarity: BADGE_RARITY.LEGENDARY,
    icon: "fa-earth-americas",
    gradient: BADGE_GRADIENTS.domainOverlord,
    requirement: { type: "min_domains", value: 20 },
    progression: BADGE_PROGRESSIONS.DOMAIN_MASTERY,
    progressionOrder: 1,
    order: 31,
  },
  {
    id: "domain_god",
    name: "Domain God",
    description: "Added 50+ service domains",
    longDescription:
      "50 DOMAINS! You've transcended mere mortal status. You are a Domain God, ruling over a vast empire of content.",
    category: BADGE_CATEGORIES.DOMAINS,
    rarity: BADGE_RARITY.MYTHIC,
    icon: "fa-globe",
    gradient: BADGE_GRADIENTS.domainGod,
    badgeType: "sunburst",
    centerGradient: ["#4B0082", "#8A2BE2", "#9370DB"],
    requirement: { type: "min_domains", value: 50 },
    progression: BADGE_PROGRESSIONS.DOMAIN_MASTERY,
    progressionOrder: 2,
    order: 32,
  },

  // ===== COMBO ACHIEVEMENTS (Standalone - Multiple Requirements) =====
  {
    id: "perfect_storm",
    name: "Perfect Storm",
    description: "100+ users, 10+ domains, 90+ days no churn",
    longDescription:
      "The perfect storm of success! 100+ users, 10+ domains, and 90+ days of perfect retention. You've mastered every aspect of reselling.",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.LEGENDARY,
    icon: "fa-bolt-lightning",
    gradient: BADGE_GRADIENTS.perfectStorm,
    requirement: {
      type: "combo",
      conditions: [
        { type: "min_users", value: 100 },
        { type: "min_domains", value: 10 },
        { type: "no_churn_days", value: 90 },
      ],
    },
    order: 33,
  },
  {
    id: "unicorn",
    name: "Unicorn",
    description: "500+ users, 1 year loyalty, top tier",
    longDescription:
      "You're a unicorn! 500+ users, over a year of loyalty, and top tier status. This mythical combination is nearly impossible to achieve.",
    category: BADGE_CATEGORIES.SPECIAL,
    rarity: BADGE_RARITY.MYTHIC,
    icon: "fa-horse-head",
    gradient: BADGE_GRADIENTS.unicorn,
    badgeType: "sunburst",
    centerGradient: ["#FF69B4", "#FF1493", "#FFD700"],
    requirement: {
      type: "combo",
      conditions: [
        { type: "min_users", value: 500 },
        { type: "min_days", value: 365 },
        { type: "min_tier_order", value: 6 },
      ],
    },
    order: 34,
  },

  // ===== TITAN PROGRESSION (Extends User Milestones) =====
  {
    id: "titan",
    name: "Titan",
    description: "Reached 2000 active users",
    longDescription:
      "2000 users! You've become a Titan in the reseller world. Your empire rivals the largest in the ecosystem.",
    category: BADGE_CATEGORIES.USERS,
    rarity: BADGE_RARITY.LEGENDARY,
    icon: "fa-mountain",
    gradient: BADGE_GRADIENTS.titan,
    requirement: { type: "min_users", value: 2000 },
    progression: BADGE_PROGRESSIONS.TITAN,
    progressionOrder: 1,
    order: 35,
  },
  {
    id: "godlike",
    name: "Godlike",
    description: "Reached 5000 active users",
    longDescription:
      "5000 USERS! You have achieved godlike status. You are among the absolute elite - a living legend in the Lunova ecosystem.",
    category: BADGE_CATEGORIES.USERS,
    rarity: BADGE_RARITY.MYTHIC,
    icon: "fa-sun",
    gradient: BADGE_GRADIENTS.godlike,
    badgeType: "sunburst",
    centerGradient: ["#FFD700", "#FFA500", "#FF4500"],
    requirement: { type: "min_users", value: 5000 },
    progression: BADGE_PROGRESSIONS.TITAN,
    progressionOrder: 2,
    order: 36,
  },
];

// Helper function to check if a badge requirement is met
export const checkBadgeRequirement = (badge, resellerStats) => {
  const { type, value } = badge.requirement;

  switch (type) {
    case "min_users":
      return (resellerStats.totalUsers || 0) >= value;

    case "min_domains":
      return (resellerStats.uniqueDomains || 0) >= value;

    case "min_days": {
      if (!resellerStats.joinedDate) return false;
      const joinDate = new Date(resellerStats.joinedDate);
      const now = new Date();
      const daysDiff = Math.floor((now - joinDate) / (1000 * 60 * 60 * 24));

      return daysDiff >= value;
    }

    case "min_tier_order":
      return (resellerStats.tierOrder || 0) >= value;

    case "joined_before": {
      if (!resellerStats.joinedDate) return false;
      const joinDate = new Date(resellerStats.joinedDate);
      const targetDate = new Date(value);

      return joinDate < targetDate;
    }

    case "weekly_growth":
      return (resellerStats.weeklyGrowth || 0) >= value;

    case "no_churn_days":
      return (resellerStats.daysWithoutChurn || 0) >= value;

    case "slow_growth": {
      // Steady grower: has users, been around for a while, but weekly growth is low (1-3 users/week avg)
      const totalUsers = resellerStats.totalUsers || 0;

      if (!resellerStats.joinedDate || totalUsers < value) return false;
      const joinDate = new Date(resellerStats.joinedDate);
      const now = new Date();
      const daysDiff = Math.floor((now - joinDate) / (1000 * 60 * 60 * 24));
      const weeksActive = Math.max(1, daysDiff / 7);
      const avgWeeklyGrowth = totalUsers / weeksActive;

      // Qualifies if they have enough users and average growth is between 0.5-5 users per week
      return avgWeeklyGrowth >= 0.5 && avgWeeklyGrowth <= 5;
    }

    case "combo": {
      // Combo badges require ALL conditions to be met
      const conditions = badge.requirement.conditions || [];

      return conditions.every((condition) => {
        // Create a temporary badge object for each condition to check
        const tempBadge = { requirement: condition };

        return checkBadgeRequirement(tempBadge, resellerStats);
      });
    }

    default:
      return false;
  }
};

// Get all earned badges for a reseller
export const getEarnedBadges = (resellerStats) =>
  BADGES.filter((badge) => checkBadgeRequirement(badge, resellerStats));

// Get all locked badges for a reseller
export const getLockedBadges = (resellerStats) =>
  BADGES.filter((badge) => !checkBadgeRequirement(badge, resellerStats));

// Get progress towards a locked badge (0-100)
export const getBadgeProgress = (badge, resellerStats) => {
  const { type, value } = badge.requirement;

  switch (type) {
    case "min_users": {
      const current = resellerStats.totalUsers || 0;

      return Math.min((current / value) * 100, 100);
    }

    case "min_domains": {
      const current = resellerStats.uniqueDomains || 0;

      return Math.min((current / value) * 100, 100);
    }

    case "min_days": {
      if (!resellerStats.joinedDate) return 0;
      const joinDate = new Date(resellerStats.joinedDate);
      const now = new Date();
      const daysDiff = Math.floor((now - joinDate) / (1000 * 60 * 60 * 24));

      return Math.min((daysDiff / value) * 100, 100);
    }

    case "min_tier_order": {
      const current = resellerStats.tierOrder || 0;

      return Math.min((current / value) * 100, 100);
    }

    case "weekly_growth": {
      const current = resellerStats.weeklyGrowth || 0;

      return Math.min((current / value) * 100, 100);
    }

    case "slow_growth": {
      // Progress based on reaching the minimum user count
      const current = resellerStats.totalUsers || 0;

      return Math.min((current / value) * 100, 100);
    }

    case "no_churn_days": {
      const current = resellerStats.daysWithoutChurn || 0;

      return Math.min((current / value) * 100, 100);
    }

    case "combo": {
      // For combo badges, calculate average progress across all conditions
      const conditions = badge.requirement.conditions || [];

      if (conditions.length === 0) return 0;
      const totalProgress = conditions.reduce((sum, condition) => {
        const tempBadge = { requirement: condition };

        return sum + getBadgeProgress(tempBadge, resellerStats);
      }, 0);

      return totalProgress / conditions.length;
    }

    default:
      return 0;
  }
};

// Get the highest earned badge in each progression (for display purposes)
// Returns only the top badge from each progression + all standalone badges
export const getDisplayBadges = (resellerStats) => {
  const earnedBadges = getEarnedBadges(resellerStats);

  // Group badges by progression
  const progressionGroups = {};
  const standaloneBadges = [];

  earnedBadges.forEach((badge) => {
    if (badge.progression) {
      if (!progressionGroups[badge.progression]) {
        progressionGroups[badge.progression] = [];
      }
      progressionGroups[badge.progression].push(badge);
    } else {
      standaloneBadges.push(badge);
    }
  });

  // Get the highest badge from each progression (highest progressionOrder)
  const highestBadges = Object.values(progressionGroups).map(
    (badges) =>
      badges.sort((a, b) => b.progressionOrder - a.progressionOrder)[0]
  );

  // Combine and sort by order
  return [...highestBadges, ...standaloneBadges].sort(
    (a, b) => a.order - b.order
  );
};

// Get all badges in a progression (both earned and locked)
export const getBadgeProgression = (badge) => {
  if (!badge.progression) return [badge];

  return BADGES.filter((b) => b.progression === badge.progression).sort(
    (a, b) => a.progressionOrder - b.progressionOrder
  );
};

// Get earned badges in a progression for a specific badge
export const getEarnedProgressionBadges = (badge, resellerStats) => {
  const progression = getBadgeProgression(badge);

  return progression.filter((b) => checkBadgeRequirement(b, resellerStats));
};

// Get the next badge in the progression (if any)
export const getNextProgressionBadge = (badge, resellerStats) => {
  if (!badge.progression) return null;

  const progression = getBadgeProgression(badge);
  const earnedBadges = getEarnedProgressionBadges(badge, resellerStats);
  const highestEarned = earnedBadges.sort(
    (a, b) => b.progressionOrder - a.progressionOrder
  )[0];

  if (!highestEarned) return progression[0];

  const nextIndex = progression.findIndex((b) => b.id === highestEarned.id) + 1;

  return nextIndex < progression.length ? progression[nextIndex] : null;
};
